import {
  Activity,
  ActivityFeedItemDefinition,
} from "@ofqual/portal-activity-feed";
import { useSelector } from "react-redux";
import { PublicEnquiryModel } from "../../../domain/PublicEnquiry";
import { authSelector } from "../../../redux/selectors";
import {
  AttachmentBodyProps,
  CaseAttachmentActivityBody,
  InternalMessage,
  PublicMessage,
  RecordTransfer
} from "./types";
import { DownloadLink } from "@ofqual/portal-components/components/DownloadLink";
import { ActivityType } from "../../../domain/ActivityType";
import ReactMarkdown from "react-markdown";
import React from "react";
import { Chip, Typography } from "@material-ui/core";
import { useLookup } from "@ofqual/portal-core/core/hooks";
import { publicEnquiryService } from "../../../services";

export const buildDefinitions = (
  setFocussedActivity: (activity: Activity) => void,
  publicEnquiry: PublicEnquiryModel,
  caseTypeSlaLookups: ReturnType<typeof useLookup>[0]
) => {
  const AttachmentBody: React.FC<AttachmentBodyProps> = ({ bodyProps }) => {
    const { body, activity } = bodyProps;
    const { token } = useSelector(authSelector);
    const buildGetBlob = (blobId: string) => () =>
      publicEnquiryService.downloadActivityAttachment(
        publicEnquiry.id,
        activity.id,
        blobId,
        {
          token,
        }
      );

    return (
      <>
        <ul style={{ padding: 0, marginLeft: 0, marginTop: 0 }}>
          {body.documents.map((doc) => (
            <li key={doc.blobId} style={{ listStyleType: "none" }}>
              {
                <DownloadLink
                  fileName={doc.fileName}
                  getBlob={buildGetBlob(doc.blobId)}
                />
              }
            </li>
          ))}
        </ul>
      </>
    );
  };

  const noteAdded: ActivityFeedItemDefinition<CaseAttachmentActivityBody> = {
    activityType: ActivityType.PublicEnqiryCreateNote,
    Body: ({ body, activity }) => (
      <>
        {body.message ? (
          <div style={{ marginBottom: 15 }}>
            <b>Subject: </b>
            <Typography>{body.subject}</Typography>
            <b>Note: </b>
            <ReactMarkdown>{body.message}</ReactMarkdown>
            <b>Response type: </b>
            {caseTypeSlaLookups.find((x) => x.id === +body.responseType)
              ?.label || ""}
          </div>
        ) : null}
        <AttachmentBody bodyProps={{ activity, body }} />
      </>
    ),
  };

  const internalMessageAdded: ActivityFeedItemDefinition<InternalMessage> = {
    activityType: ActivityType.PublicEnquiryInternalMessage,
    Body: ({ body, activity }) => (
      <>
        {body.message ? (
          <div style={{ marginBottom: 15 }}>
            <b>Subject: </b>
            <Typography>{body.subject}</Typography>
            <b>Message: </b>
            <ReactMarkdown>{body.message}</ReactMarkdown>
            <b>Recipients: </b>
            <>
              {body.upns.map((r) => (
                <Chip label={r} color="secondary" />
              ))}
            </>
          </div>
        ) : null}
        <AttachmentBody bodyProps={{ activity, body }} />
      </>
    ),
  };

  const publicMessageAdded: ActivityFeedItemDefinition<PublicMessage> = {
    activityType: ActivityType.PublicEnquiryPublicMessage,
    Body: ({ body, activity }) => (
      <>
        {body.message ? (
          <div style={{ marginBottom: 15 }}>
            <b>Subject: </b>
            <Typography>{body.subject}</Typography>
            <b>Message: </b>
            <ReactMarkdown>{body.message}</ReactMarkdown>
            <b>Sender: </b>
            <>
              {body.isOfqual ? (
                <Chip label="Ofqual" color="secondary" />
              ) : (
                <Chip label="Citizen" color="secondary" />
              )}
            </>
          </div>
        ) : null}
        <AttachmentBody bodyProps={{ activity, body }} />
      </>
    ),
  };

  const recordTransferred: ActivityFeedItemDefinition<RecordTransfer> = {
    activityType: ActivityType.FoiCreatedFromPublicEnquiry,
    Body: ({ body, activity }) => (
      <>
        {body.action ? (
          <div style={{ marginBottom: 15 }}>
            <b>Action: </b>
            <Typography>{body.action}</Typography>
            <b>Auth: </b>
            <Typography>{body.upn}</Typography>
          </div>
        ) : null}
      </>
    ),
  };


  // const closed: ActivityFeedItemDefinition<ClosedActivityBody> = {
  //   activityType: ActivityType.PublicEnquiryClosed,
  //   Body: ({ body }) => (
  //     <>
  //       <div style={{ marginBottom: 15 }}>
  //         <b>Shared with IfATE: </b>
  //         {body.sharedWithIfate === true ? "Yes" : "No"}
  //       </div>
  //     </>
  //   ),
  // };

  return [
    noteAdded,
    internalMessageAdded,
    publicMessageAdded,
    recordTransferred,
  ];
};
